import { useState, ReactElement, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { CheckoutComponent, CheckoutFormSkeleton, ContentModal } from '@localstack/ui';
import { VAT_COUNTRIES } from '@localstack/constants';

import { Box, CircularProgress, Typography } from '@mui/material';

import {
  Plan,
  EmulatorType,
  SubscriptionStatus,
  InvoiceStatus,
  Discount,
  OrderContext,
  OrganizationRestrictionReason,
  Subscription,
  DocumentType,
  LegalAcceptanceSource,
} from '@localstack/types';

import {
  useRoutes,
  useApiGetter,
  useApiEffect,
  LegalService,
  SubscriptionService,
  UserService,
  OrganizationService,
  MembershipService,
  OrganizationsService,
} from '@localstack/services';

import { ContainedCustomerLayout } from '~/layouts';
import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import { StripeCardForm } from '~/components';
import { EMULATOR_QUERY_PARAM } from '~/util/pricing';

export const PricingSubscribe = (): ReactElement => {
  const { subscriptionId } = useParams<'subscriptionId'>() as { subscriptionId?: string };

  const { goto } = useRoutes();
  const { reloadUserInfo, userInfo } = useAuthProvider();

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const planId = query.get('plan');
  const emulator = (query.get(EMULATOR_QUERY_PARAM) as EmulatorType) || undefined;

  const [promotion, setPromotion] = useState<Optional<Discount>>();
  const [seats, setSeats] = useState<Optional<number>>();
  const [plan, setPlan] = useState<Optional<Plan>>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [showCompletePaymentDialog, setShowCompletePaymentDialog] = useState(false);
  const [newSubscriptionId, setNewSubscriptionId] = useState<Optional<string>>(undefined);

  const { data: invoices, isLoading: isInvoicesLoading } = useApiGetter(SubscriptionService, 'listInvoices', []);

  const openInvoice = useMemo(
    () => invoices?.find((i) => i.status === InvoiceStatus.OPEN && i.subscription === newSubscriptionId),
    [invoices, showCompletePaymentDialog, newSubscriptionId],
  );

  const {
    attachCreditCard,
    removeCreditCard,
    isLoading: isCreditCardMutating,
  } = useApiEffect(UserService, ['attachCreditCard', 'removeCreditCard'], { revalidate: ['listCreditCards'] });

  const { getPromotion, isLoading: isPromotionLoading } = useApiEffect(SubscriptionService, ['getPromotion']);
  const { data: plans, isLoading: isPlansLoading } = useApiGetter(SubscriptionService, 'listPlans', [emulator]);
  const { data: cards } = useApiGetter(UserService, 'listCreditCards', []);
  const { data: orgRestrictions } = useApiGetter(OrganizationsService, 'listOrganizationRestrictions', [], {
    defaultValue: [],
  });

  const { data: members } = useApiGetter(MembershipService, 'listMembers', [], { defaultValue: [] });
  const { data: organization } = useApiGetter(OrganizationService, 'getOrganization', []);
  const orgCountry = organization?.billing_details.country;

  const { data: subscription, isLoading: isSubLoading } = useApiGetter(
    SubscriptionService,
    'getSubscription',
    [subscriptionId ?? ''],
    { enable: !!subscriptionId },
  );

  const { data: licenseAssignments, isLoading: isLicensesLoading } = useApiGetter(
    OrganizationsService,
    'listLicenseAssignments',
    ['my'],
    { enable: !!userInfo },
  );

  const subscriptionLicenceAssignments = licenseAssignments?.filter(
    (license) => license.subscription_ids?.[0] === subscriptionId,
  );

  const orderedSeats = seats ?? subscription?.seats ?? 1;

  const { data: tax, isLoading: isTaxLoading } = useApiGetter(
    SubscriptionService,
    'calculateTax',
    [plan?.id ?? '', orderedSeats, undefined],
    {
      defaultValue: null,
      enable: !!(plan && orgCountry && VAT_COUNTRIES.includes(orgCountry)),
    },
  );

  const {
    createSubscription,
    updateSubscription,
    isLoading: isSubMutating,
  } = useApiEffect(SubscriptionService, ['createSubscription', 'updateSubscription'], {
    revalidate: [
      'listSubscriptions',
      'getSubscription',
      'listInvoices',
      'listPlans',
      'listCiTokens',
      'getAggregatedSubscriptionInfo',
    ],
    revalidateOtherClient: {
      client: [OrganizationsService, UserService],
      methods: ['listLicenseAssignments', 'listLicenseAssignmentsForUser', 'getUser'],
    },
  });

  const { patchOrganization, isLoading: isOrgMutating } = useApiEffect(OrganizationService, ['patchOrganization'], {
    revalidate: ['getOrganization'],
  });

  const { unassignLicense, isLoading: isUnassigningLicense } = useApiEffect(OrganizationsService, ['unassignLicense'], {
    revalidate: ['listLicenseAssignments'],
  });

  const { recordLegalAcceptance } = useApiEffect(LegalService, ['recordLegalAcceptance']);

  const goToBilling = () => {
    goto(AppRoute.SETTINGS_BILLING);
    reloadUserInfo();
  };

  const onApplyPromoCode = async (promotionPlanId: string, code: Optional<string>) => {
    setPromotion(code ? await getPromotion(promotionPlanId, code) : undefined);
  };

  const onAddNewCard = async (cardId: string, currency: string) => {
    // TODO: think how we can pre-select the card without exposing the state
    await attachCreditCard({ token: cardId, currency });
  };

  const onSubscribe = async (
    subscribedPlanId: string,
    _productIds: string[],
    context: OrderContext,
    subId?: string,
    _discountId?: string,
  ) => {
    // fire up the call early, and await it before we move away from the page.
    // to make sure it is not cancelled by the browser.
    const legalAcceptancePromise = recordLegalAcceptance({
      document_type: DocumentType.MSA,
      source: LegalAcceptanceSource.CHECKOUT,
      context: 'Implicitly accepted MSA when finishing checkout',
    });

    let result: Optional<Subscription>;
    if (subId) {
      result = await updateSubscription(subId, { plan: subscribedPlanId, ...context });
    } else {
      result = await createSubscription({ plan: subscribedPlanId, ...context });
    }

    await legalAcceptancePromise;

    if ([SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(result.status)) {
      goToBilling();
    } else if (result.status === SubscriptionStatus.INCOMPLETE) {
      setShowCompletePaymentDialog(true);
      setNewSubscriptionId(result.id);
    }
  };

  const isPageLoading = isPlansLoading || (subscriptionId && isSubLoading);

  const isLoading = isPlansLoading || isLicensesLoading || isOrgMutating || isUnassigningLicense;

  return (
    <ContainedCustomerLayout title={subscriptionId ? 'Update Subscription Terms' : 'Subscribe to a LocalStack Plan'}>
      {isPageLoading && <CheckoutFormSkeleton />}

      {!isPageLoading && (
        <CheckoutComponent
          loading={isLoading}
          isSubMutating={isSubMutating}
          taxLoading={isTaxLoading}
          tax={tax}
          defaultPlan={(plans ?? []).find((p) => p.id === planId)}
          plans={plans ?? []}
          cards={cards ?? []}
          subscription={subscription}
          subscriptionLicenseAssignments={subscriptionLicenceAssignments}
          organization={organization}
          members={members}
          isFreemiumHobbyCommercialMisuse={
            orgRestrictions?.some((r) => r.reason === OrganizationRestrictionReason.FREEMIUM_HOBBY_COMMERCIAL_MISUSE) ??
            false
          }
          promotion={promotion}
          promotionLoading={isPromotionLoading}
          showCardForm={showCardForm}
          onChangeSeats={setSeats}
          onChangePlan={setPlan}
          onToggleCardForm={() => setShowCardForm(!showCardForm)}
          onApplyPromoCode={onApplyPromoCode}
          onUpdateOrganization={patchOrganization}
          onDeleteCard={(card) => removeCreditCard(card.id)}
          onDeleteLicense={(licenseId) => unassignLicense(organization?.id || '', licenseId)}
          onSubscribe={onSubscribe}
          renderCardForm={(currency: string) => (
            <Box p={2}>
              <StripeCardForm
                loading={stripeLoading || isCreditCardMutating}
                onSaveCard={async (cardId) => {
                  try {
                    setStripeLoading(true);
                    await onAddNewCard(cardId, currency);
                    setShowCardForm(false);
                  } finally {
                    setStripeLoading(false);
                  }
                }}
              />
            </Box>
          )}
        />
      )}
      <ContentModal
        open={showCompletePaymentDialog}
        dialogTitle="Complete Payment"
        onConfirm={() => goToBilling()}
        confirmButtonText="Pay"
        confirmButtonProps={{
          href: openInvoice?.hosted_invoice_url || '',
          target: '_blank',
          disabled: !openInvoice,
        }}
        dialogContent={
          <>
            <Typography>Your provided credit card requires further verification.</Typography>
            <Typography>Please proceed by clicking on the below button.</Typography>
            {isInvoicesLoading && (
              <Box mt={2}>
                <CircularProgress />
              </Box>
            )}
            {openInvoice && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1">Invoice: {openInvoice.number}</Typography>
              </Box>
            )}
          </>
        }
      />
    </ContainedCustomerLayout>
  );
};
